import type { ReactNode } from "react";
import { format } from "date-fns";
import {
  getCalendarPresetLabel,
  type UserTzContext,
} from "./TimeWindow.calendarPresets";
import { DATE_FORMAT, SHORT_DATE_FORMAT } from "./TimeWindow.constants";
import {
  getTimeWindowSelectionLabel,
  parseTimeWindowSelection,
} from "./TimeWindow.selection";
import { urlTimeWindow } from "./TimeWindow.urlSerialization";

export const getDateRangeLabels = (
  dateRange: [Date, Date]
): [string, string] | undefined => {
  const [from, to] = dateRange;

  if (!from || !to) return;

  const isSameYear = from.getFullYear() === to.getFullYear();
  const fromStr = format(from, isSameYear ? SHORT_DATE_FORMAT : DATE_FORMAT);
  const toStr = format(to, DATE_FORMAT);

  return [fromStr, toStr];
};

export const getCustomTimeWindowLabel = (dateRange: [Date, Date]): string => {
  const rangeStrings = getDateRangeLabels(dateRange);
  return rangeStrings ? `From ${rangeStrings[0]} to ${rangeStrings[1]}` : "";
};

export const getShortTimeWindowLabel = (dateRange: [Date, Date]): string => {
  const rangeStrings = getDateRangeLabels(dateRange);
  return rangeStrings ? `${rangeStrings[0]} - ${rangeStrings[1]}` : "";
};

const TzSuffix = ({ tzStr }: { tzStr?: string }) => {
  return tzStr ? (
    <div className="inline-block text-xs px-1 py-0.5 -mr-2 bg-neutral-lightest dark:bg-neutral-dark text-text-secondary rounded-md ml-2">
      UTC {tzStr}
    </div>
  ) : (
    ""
  );
};

const tzSuffixText = (tzStr?: string) => (tzStr ? ` (UTC ${tzStr})` : "");

const INVALID_LABEL = "Invalid date range";

/**
 * Base field label (no timezone suffix). `isToken` says whether the value was a
 * selection token, which renders as plain text rather than with the badge.
 */
const resolveTimeWindowFieldLabelBase = (
  selectedValue: string,
  formatter: (dateRange: [Date, Date]) => string,
  ctx?: UserTzContext
): { label: string; isToken: boolean } => {
  const parsed = parseTimeWindowSelection(selectedValue);
  if (parsed?.kind === "duration" || parsed?.kind === "named") {
    return { label: getTimeWindowSelectionLabel(selectedValue), isToken: true };
  }

  const calendarLabel = getCalendarPresetLabel(selectedValue, ctx);
  if (calendarLabel) {
    return { label: calendarLabel, isToken: false };
  }

  const dateRange = urlTimeWindow.deserializeDateRangeStr(selectedValue);
  if (dateRange?.length !== 2) {
    return { label: INVALID_LABEL, isToken: false };
  }

  try {
    return { label: formatter(dateRange as [Date, Date]), isToken: false };
  } catch (e) {
    console.error("Error formatting date range:", e);
    return { label: INVALID_LABEL, isToken: false };
  }
};

/**
 * Plain-string field label (presets, calendar, custom ranges) with optional text tz suffix.
 * Use for non-UI consumers (e.g. AI chat context).
 */
export const getTimeWindowFieldLabelText = (
  selectedValue: string,
  tzStr?: string,
  formatter: (dateRange: [Date, Date]) => string = getCustomTimeWindowLabel,
  ctx?: UserTzContext
): string => {
  const { label } = resolveTimeWindowFieldLabelBase(selectedValue, formatter, ctx);

  return label === INVALID_LABEL ? label : label + tzSuffixText(tzStr);
};

/**
 * Human-readable time window field label for UI.
 * Tokens/invalid → string with optional text tz; calendar/custom → label + TzSuffix badge.
 */
export const getTimeWindowFieldLabel = (
  selectedValue: string,
  tzStr?: string,
  formatter: (dateRange: [Date, Date]) => string = getCustomTimeWindowLabel,
  ctx?: UserTzContext
): ReactNode => {
  const { label, isToken } = resolveTimeWindowFieldLabelBase(
    selectedValue,
    formatter,
    ctx
  );

  if (isToken) {
    return label + tzSuffixText(tzStr);
  }

  if (label === INVALID_LABEL) {
    return label;
  }

  return (
    <>
      {label}
      <TzSuffix tzStr={tzStr} />
    </>
  );
};
